import { useEoState } from '~/src/hooks/useEoState'
import {
  AUTHENTICATION_FLOW_STATE,
  AuthenticationFlowState,
  authenticationFlowStateDefaultValue
} from '~/src/stores/authentication-flow'
import useDialog from '~/src/hooks/useDialog'
import LoginDialog from '~/src/components/auth/dialogs/login/LoginDialog'
import LoginTypeSelectionDialog from '~/src/components/auth/dialogs/login-type-selection/LoginTypeSelectionDialog'
import useAuthenticationFlowLoginTypeSelectionOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowLoginTypeSelectionOnSubmit'
import useAuthenticationFlowLoginOnSubmit from '~/src/hooks/auth/flow/useAuthenticationFlowLoginOnSubmit'
import useAuthenticationFlowCustomerMissingFieldsOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowCustomerMissingFieldsOnSubmit'
import useAuthenticationFlowConfirmationCodeOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowConfirmationCodeOnSubmit'
import useAuthenticationFlowRegisterOnSubmit from '~/src/hooks/auth/flow/useAuthenticationFlowRegisterOnSubmit'
import CustomerMissingFieldsDialog from '~/src/components/auth/dialogs/missing-fields/CustomerMissingFieldsDialog'
import ConfirmationCodeDialog from '~/src/components/auth/dialogs/confirmation-code/ConfirmationCodeDialog'
import RegisterDialog from '~/src/components/auth/dialogs/register/RegisterDialog'
import RegisterConfirmationDialog from '~/src/components/auth/dialogs/register-confirmation/RegisterConfirmationDialog'
import useAuthenticationFlowGuestMissingFieldsOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowGuestMissingFieldsOnSubmit'
import GuestMissingFieldsDialog from '~/src/components/auth/dialogs/missing-fields/GuestMissingFieldsDialog'
import { AuthenticationFlowDialogNameEnum } from '~/src/types/DialogNameEnum'
import RegisterSuccessfulDialog
  from '~/src/components/auth/dialogs/register-successful/RegisterSuccessfulDialog'
import useAuthenticationFlowRegisterSuccessfulOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowRegisterSuccessfulOnSubmit'
import ResetPasswordDialog from '~/src/components/auth/dialogs/reset-password/ResetPasswordDialog'
import useAuthenticationFlowResetPasswordOnSubmit
  from '~/src/hooks/auth/flow/useAuthenticationFlowResetPasswordOnSubmit'
import useGetQueryParam from '~/src/hooks/router/useGetQueryParam'
import Params from '~/src/router/enums/params.enum'
import RoutesEnum, { RoutesEnumType } from '~/src/router/enums/routes.enum'
import { useRouterPush } from '~/src/hooks/router/useRouterPush'
import { useFirmPathResolver } from '~/src/hooks/router/useFirmPathResolver'
import { FC } from 'react'

export interface UseAuthenticationFlowDialogsReturnType {
  restart(): void,
  back(): void,
  proceed(): void,
  next(): void,
  goToStep<T = any>(step: AuthenticationFlowDialogNameEnum, dialogProps?: T): void,
  end(resetState?: boolean): void,
  reset(): void,
}

const useAuthenticationFlowDialogs = (): UseAuthenticationFlowDialogsReturnType => {
  const { dismiss, present } = useDialog()
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { push } = useRouterPush()
  const { resolve } = useFirmPathResolver()
  const { params: redirectTo } = useGetQueryParam(Params.REDIRECT_TO)

  const goToStep = <T = any>(step: AuthenticationFlowDialogNameEnum, dialogProps?: T): void => {
    _setStep(step, dialogProps)
  }
  
  const _setStep = (step: AuthenticationFlowDialogNameEnum | null, dialogProps?: any): void => {
    if (!step) {
      return
    }

    dismiss(authenticationFlowState.step)
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      step,
      previousStep: state.step
    }))
    _openDialogBasedOnStep(step, dialogProps)
  }


  const restart = (): void => {
    dismiss(authenticationFlowState.step)
    reset()
    _openDialogBasedOnStep(authenticationFlowStateDefaultValue.step)
  }

  const back = (): void => {
    dismiss(authenticationFlowState.step)
    _setStep(authenticationFlowState.previousStep)
  }

  const proceed = (): void => {
    _openDialogBasedOnStep(authenticationFlowState.step)
  }

  const reset = (): void => {
    setAuthenticationFlowState(authenticationFlowStateDefaultValue)
  }

  const end = (resetState = false) => {
    if (redirectTo) {
      if (redirectTo === 'ACCOUNT') {
        push(resolve(`${RoutesEnum.PROFILE}/${RoutesEnum.ACCOUNT}`))
      } else {
        push(resolve(RoutesEnum[redirectTo as keyof RoutesEnumType]))
      }
    }

    dismiss(authenticationFlowState.step)
    resetState && reset()
  }

  const { onSubmit: loginTypeSelectionOnSubmit } = useAuthenticationFlowLoginTypeSelectionOnSubmit({ goToStep, end })
  const { onSubmit: loginOnSubmit } = useAuthenticationFlowLoginOnSubmit({ end })
  const { onSubmit: customerMissingFieldsOnSubmit } = useAuthenticationFlowCustomerMissingFieldsOnSubmit({ end })
  const { onSubmit: guestMissingFieldsOnSubmit } = useAuthenticationFlowGuestMissingFieldsOnSubmit({ end })
  const { onSubmit: confirmationCodeOnSubmit } = useAuthenticationFlowConfirmationCodeOnSubmit({ goToStep, end })
  const { onSubmit: registerOnSubmit } = useAuthenticationFlowRegisterOnSubmit({ goToStep })
  const { onSubmit: registerSuccessfulOnSubmit } = useAuthenticationFlowRegisterSuccessfulOnSubmit({ goToStep })
  const { onSubmit: resetPasswordOnSubmit } = useAuthenticationFlowResetPasswordOnSubmit({ goToStep, restart })

  const next = async (): Promise<void | boolean> => {
    switch (authenticationFlowState.step) {
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_LOGIN_TYPE_SELECTION:
        return loginTypeSelectionOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_LOGIN:
        return loginOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CUSTOMER_MISSING_FIELDS:
        return customerMissingFieldsOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_GUEST_MISSING_FIELDS:
        return guestMissingFieldsOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CONFIRMATION_CODE:
        return confirmationCodeOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_REGISTER:
        return registerOnSubmit()
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_RESET_PASSWORD:
        return resetPasswordOnSubmit()
      default:
        return registerSuccessfulOnSubmit()
    }
  }

  const _openDialogBasedOnStep = (step: AuthenticationFlowDialogNameEnum, dialogProps?: any): void => {
    let dialog: FC | undefined

    switch (step) {
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_LOGIN_TYPE_SELECTION:
        dialog = LoginTypeSelectionDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_LOGIN:
        dialog = LoginDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CUSTOMER_MISSING_FIELDS:
        dialog = CustomerMissingFieldsDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_GUEST_MISSING_FIELDS:
        dialog = GuestMissingFieldsDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CONFIRMATION_CODE:
        dialog = ConfirmationCodeDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_REGISTER:
        dialog = RegisterDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_REGISTER_CONFIRMATION:
        dialog = RegisterConfirmationDialog
        break
      case AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_RESET_PASSWORD:
        dialog = ResetPasswordDialog
        break
      default:
        dialog = RegisterSuccessfulDialog
    }

    dialog && present(dialog, step, dialogProps)
  }
  
  return {
    restart,
    back,
    proceed,
    goToStep,
    next,
    end,
    reset
  }
}

export default useAuthenticationFlowDialogs
